/**
 * Which questions, options, matrix rows and statements a respondent sees.
 *
 * A copy of my-dq-api src/services/survey/survey_rules.svy.js — keep the two in step,
 * or the API will drop answers this page showed.
 *
 * `selected` is { [question_id]: [codes] } for single/multi/rank questions.
 *   { q, in: [codes] }            selected[q] contains one of the codes
 *   { q, first_not_in: [codes] }  selected[q] has a first entry, and it is not one of the codes
 *
 * Required questions (`required: true`): single needs one option; multi and rank need
 * at least `min` (default 1, never more than the options shown); a matrix needs every
 * row shown to have a statement and a rating.
 */

export const isRuleMet = (rule, selected) => {
    if (!rule) return true;
    const codes = selected[rule.q] || [];
    if (Array.isArray(rule.in)) return codes.some((c) => rule.in.includes(c));
    if (Array.isArray(rule.first_not_in)) return codes.length > 0 && !rule.first_not_in.includes(codes[0]);
    return true;
};

export const visibleQuestions = (definition, selected) =>
    (definition.questions || []).filter((q) => isRuleMet(q.show_if, selected));

export const visibleOptions = (question, selected) =>
    (question.options || []).filter((o) => isRuleMet(o.show_if, selected));

export const matrixRows = (question, selected) => {
    const { q, take } = question.rows_from || {};
    return (selected[q] || [])
        .slice(0, take || undefined)
        .map((code) => (question.rows || []).find((r) => r.code === code))
        .filter(Boolean);
};

export const visibleStatements = (row, selected) =>
    (row.statements || []).filter((s) => isRuleMet(s.show_if, selected));

/** How many options a required single/multi/rank question needs, given how many are shown. */
export const minChoices = (question, visibleCount) => {
    if (question.type === 'single') return Math.min(1, visibleCount);
    return Math.min(question.min || 1, visibleCount);
};
